import type { Identifier, UserIdentity } from 'react-admin';
import type { SupabaseClient } from '@supabase/supabase-js';
import { SignUpData } from '@domain/types';
import type { getIsInitialized, getIdentity } from './authProvider';

type AuthModule = {
    getIsInitialized: typeof getIsInitialized;
    getIdentity: typeof getIdentity;
};

export function createAccountMethods(
    supabase: SupabaseClient,
    auth: AuthModule
) {
    return {
        async signUp({ email, password, first_name, last_name }: SignUpData) {
            const response = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        first_name,
                        last_name,
                    },
                },
            });

            if (!response.data?.user || response.error) {
                console.error('signUp.error', response.error);
                throw new Error('Failed to create account');
            }

            // Update the is initialized cache
            auth.getIsInitialized._is_initialized_cache = true;

            return {
                id: response.data.user.id,
                email,
                password,
            };
        },
        async updatePassword(id: Identifier) {
            const { data: passwordUpdated, error } =
                await supabase.functions.invoke<boolean>('updatePassword', {
                    method: 'PATCH',
                    body: {
                        memberId: id,
                    },
                });

            if (!passwordUpdated || error) {
                console.error('passwordUpdate.error', error);
                throw new Error('Failed to update password');
            }

            return passwordUpdated;
        },
        async isInitialized() {
            return auth.getIsInitialized();
        },
        async getIdentity(): Promise<UserIdentity> {
            return auth.getIdentity();
        },
    };
}
